<template>
    <div
        class="JsonSchemaCountryEnumForm JsonSchemaEnumForm"
        :class="{
            'JsonSchemaCountryEnumForm--focused': focused,
            'JsonSchemaEnumForm--focused': focused,
        }"
    >
        <FormElement :label-for="id" :label="form.schema.title" :required="required">
            <template #requiredIndicator>
                {{ translate('requiredLabel') }}
            </template>
            <template #labelHint>
                <div v-if="form.schema.warning" class="JsonSchemaForm__warning">
                    {{ form.schema.warning }}
                </div>
                <div v-else-if="form.schema.hint" class="JsonSchemaForm__hint">
                    {{ form.schema.hint }}
                </div>
            </template>

            <FormInput :invalid="accessed && hasError" force-error-slot force-action-slot>
                <Dropdown
                    disable-lazy-load
                    :options="options"
                    :selected="currentOption"
                    :disabled="disabled"
                    :placeholder="form.schema.description || description"
                    @input="onInput"
                    @blur="touch"
                    @focus="onFocus"
                >
                    <template #prepend>
                        <CountryFlag :country="currentCountryValue" />
                    </template>
                </Dropdown>
                <template v-if="accessed && error" #errorMessage>
                    {{ error }}
                </template>

                <button
                    v-if="!required && currentOption.length > 0"
                    class="JsonSchemaCountryEnumForm__clear"
                    @click="clearValue"
                >
                    <FontAwesomeIcon :icon="faXmark" />
                    <span class="JsonSchemaCountryEnumForm__clearText">Clear</span>
                </button>

                <template v-if="deletable" #actionButtons>
                    <NewButton
                        variant="flat"
                        size="icon-sm"
                        color="secondary"
                        class="JsonSchemaForm__deleteIcon"
                        @click="$emit('delete')"
                    >
                        <FontAwesomeIcon :icon="faXmark" />
                    </NewButton>
                </template>
            </FormInput>
        </FormElement>
    </div>
</template>

<script lang="ts">
    import CountryFlag from 'vue-country-flag-next';
    import { Component, Watch } from 'vue-property-decorator';

    import { faXmark } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { JsonFormBase } from '@evidentid/json-schema/interfaces/JsonForm';
    import { JsonSchemaEnum } from '@evidentid/json-schema/interfaces/JsonSchema';

    import { Dropdown } from '../../Dropdown';
    import { DropdownOption } from '../../Dropdown/types';
    import { FormElement, FormInput } from '../../Form';
    import NewButton from '../../new-button/NewButton.vue';
    import AbstractJsonSchemaForm from '../elements/AbstractJsonSchemaForm';
    // PRODUCT-26110 Task 8b Step 4: vue-country-flag is Vue-2-only; vue-country-flag-next
    // is the same author's Vue 3 fork, a drop-in replacement (identical `country`/`size`/
    // `rounded`/`shadow`/`background` props, same default export name).

    @Component({
        emits: [ 'delete', 'input' ],
        components: { CountryFlag, Dropdown, FormInput, FormElement, FontAwesomeIcon, NewButton },
        inheritAttrs: false,
    })

    export default class JsonSchemaCountryEnumForm
        extends AbstractJsonSchemaForm<JsonFormBase<JsonSchemaEnum>, string> {
        private faXmark = faXmark;

        private options: DropdownOption[] = [];

        @Watch('form.schema.enum', { immediate: true })
        private onEnumChange(): void {
            const labels = this.form.schema.enumLabels && this.form.schema.enumLabels.length > 0
                ? this.form.schema.enumLabels
                : null;
            this.options = labels
                ? this.form.schema.enum.map((value, i) => ({ value, label: labels[i] }))
                : this.form.schema.enum.map(this.buildOptionFromValue);
        }

        private get currentOption(): DropdownOption[] {
            const option = this.options.find((option) => option.value === this.value);
            return option ? [ option ] : [];
        }

        private get currentCountryValue(): string | null {
            return this.currentOption[0]?.value || '';
        }

        private buildOptionFromValue(value: any): DropdownOption {
            return { value, label: value };
        }

        private onInput(option: DropdownOption): void {
            this.$emit('input', option.value);
            this.touch();
        }

        private clearValue() {
            this.$emit('input', this.form.getValue(undefined));
        }
    }
</script>
